import React from 'react';

import { cx, Skeleton } from '@bolteu/clay-react';
import { useIntl } from 'react-intl';

interface StoreListItemData {
  storeName: string;
  storeId: number;
}

export enum Availability {
  ONLINE = 'online',
  OFFLINE = 'offline',
  BUSY = 'busy',
}

const AVAILABILITY_INTL_LABEL: Record<Availability, string> = {
  online: 'mp2Components.storeSelector.availability.online',
  offline: 'mp2Components.storeSelector.availability.offline',
  busy: 'mp2Components.storeSelector.availability.busy',
};

const AVAILABILITY_BADGE_CLASS: Record<Availability, string> = {
  online: 'bg-positive-secondary text-positive-primary',
  offline: 'bg-neutral-secondary text-neutral-primary',
  busy: 'bg-warning-secondary text-warning-primary',
};

interface StoreListItemProps extends StoreListItemData {
  availability?: Availability;
  availabilityLoading: boolean;
  checked: boolean;
  onSelect: (storeId: number) => void;
}
const StoreListItem: React.FC<StoreListItemProps> = ({
  storeName,
  storeId,
  availability,
  availabilityLoading,
  checked,
  onSelect,
}) => {
  const intl = useIntl();

  const handleSelect = () => {
    onSelect(storeId);
  };

  const availabilityBox = () => {
    if (availability) {
      return (
        <div
          className={cx(
            'w-fit rounded-[20px] px-2 py-1 bolt-font-body-xs-regular',
            AVAILABILITY_BADGE_CLASS[availability],
          )}
        >
          {intl.formatMessage({ id: AVAILABILITY_INTL_LABEL[availability] })}
        </div>
      );
    }

    if (availabilityLoading) {
      return <Skeleton variant="rounded" className="mb-1 h-5 w-[42px]" />;
    }

    return null;
  };

  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={handleSelect}
      className="group flex w-full cursor-pointer flex-col items-start border-0 bg-transparent py-[6px] pr-4 pl-0 text-left max-sm:min-h-12 focus-visible:rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:[outline-color:var(--color-bg-action-primary)]"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={cx(
            'm-0 flex size-6 shrink-0 rounded-full border-solid duration-150 ease-linear',
            checked
              ? 'border-[7px] bg-static-key-light [.group:not(:hover)_&]:border-action-primary group-hover:border-active-action-primary'
              : 'border-2 border-neutral-primary bg-special-nulled',
          )}
        />
        <span className="bolt-font-body-m-regular text-primary [word-break:break-word]">
          {storeName}
        </span>
      </div>
      <div className="pt-1 pl-9">{availabilityBox()}</div>
    </button>
  );
};

export interface StoreListGroup {
  parentName: string;
  stores: StoreListItemData[];
}

export interface StoreSelectorListProps {
  stores: StoreListGroup[];
  storeAvailabilities: Map<number, Availability>;
  storeAvailabilitiesLoading: boolean;
  selectedStoreId?: number;
  onSelectStore?: (storeId: number) => void;
}

export function StoreSelectorList({
  stores,
  storeAvailabilities,
  storeAvailabilitiesLoading,
  selectedStoreId,
  onSelectStore,
}: StoreSelectorListProps) {
  const handleSelectStore = (storeId: number) => {
    if (onSelectStore) {
      onSelectStore(storeId);
    }
  };

  const renderStoreList = stores.map((storeGroup) => (
    <div key={storeGroup.parentName} role="radiogroup" aria-label={storeGroup.parentName}>
      <div className="pt-3 pb-2 bolt-font-body-l-accent text-primary">{storeGroup.parentName}</div>

      {storeGroup.stores.map((store) => (
        <StoreListItem
          key={store.storeId}
          storeName={store.storeName}
          availability={storeAvailabilities.get(store.storeId)}
          availabilityLoading={storeAvailabilitiesLoading}
          storeId={store.storeId}
          onSelect={handleSelectStore}
          checked={store.storeId === selectedStoreId}
        />
      ))}
    </div>
  ));
  return <div>{renderStoreList}</div>;
}
